import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../store/auth';
import { api } from '../api/client';

/** How long somebody may be away before the till locks itself. */
const IDLE_LIMIT_MS = 30 * 60_000;
/** How long they get to say "I'm still here" before it happens. */
const WARNING_MS = 2 * 60_000;

/**
 * Stay signed in while working; sign out when not.
 *
 * A shop counter is not a private desk — anybody can walk behind it while the
 * till is unattended, and a session that lasts all day because somebody left
 * the browser open is the same as no password at all.
 *
 * So the session follows the person rather than the clock: any typing, click or
 * scroll keeps it alive and quietly renews the token before it expires, and
 * thirty minutes of nothing signs them out. A warning comes two minutes before,
 * because being thrown out mid-invoice with no notice is its own kind of
 * unusable — and two minutes is long enough to notice it across a counter.
 */
export function useIdleSession(): { secondsLeft: number | null; stayIn: () => void } {
  const session = useAuth((s) => s.session);
  const signOut = useAuth((s) => s.signOut);
  const lastActive = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!session) return;

    const touch = (): void => {
      lastActive.current = Date.now();
      setSecondsLeft(null);
    };

    // Scroll and touch count as much as typing: reading a long report is
    // working, and being signed out for it would be absurd.
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });

    const tick = setInterval(() => {
      const idle = Date.now() - lastActive.current;

      if (idle >= IDLE_LIMIT_MS) {
        signOut();
        return;
      }

      if (idle >= IDLE_LIMIT_MS - WARNING_MS) {
        setSecondsLeft(Math.ceil((IDLE_LIMIT_MS - idle) / 1000));
      } else {
        setSecondsLeft(null);
      }
    }, 1000);

    /**
     * Renewed on a timer rather than after a request has already failed.
     * Waiting for a 401 means the first thing a person does after a quiet spell
     * is slower than everything else they do, for no reason they can see.
     */
    const renew = setInterval(
      () => {
        if (Date.now() - lastActive.current < IDLE_LIMIT_MS) {
          void api.get('/v1/auth/me').catch(() => undefined);
        }
      },
      10 * 60_000,
    );

    return () => {
      for (const e of events) window.removeEventListener(e, touch);
      clearInterval(tick);
      clearInterval(renew);
    };
  }, [session, signOut]);

  return {
    secondsLeft,
    stayIn: () => {
      lastActive.current = Date.now();
      setSecondsLeft(null);
    },
  };
}
